import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb } from './ui';
import { BreadcrumbItem } from '../types';
import { useShopping } from '../contexts/ShoppingContext';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { useCallback, useRef } from 'react';

const LS_SORT_KEY = 'preferredSort';

interface ProductHeaderProps {
  total: number;
}

export function ProductHeader({ total }: ProductHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { clientData } = useClientDataContext();
  const { state, departmentsLoading } = useShopping();

  const urlParams = new URLSearchParams(location.search);
  const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : undefined;
  const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : undefined;
  const searchQuery = urlParams.get('search') || undefined;
  const sortFromUrl = urlParams.get('sort') || localStorage.getItem(LS_SORT_KEY) || clientData?.defaultProductSort || 'prioritized';

  const selectedDepartment = state.departments?.find(d => d.id === selectedDepartmentId);
  const selectedCategory = state.departments
    ?.flatMap(d => d.productCategories || [])
    .find(c => c.id === selectedCategoryId);

  // Track whether departments have resolved at least once
  const deptEverReadyRef = useRef(false);
  if (!departmentsLoading) deptEverReadyRef.current = true;

  const isLoading = (!!selectedDepartmentId || !!selectedCategoryId)
    && (!deptEverReadyRef.current || (departmentsLoading && !selectedDepartment));

  const breadcrumbItems: BreadcrumbItem[] = [{ label: 'Home', href: '/' }];
  if (selectedDepartment) {
    breadcrumbItems.push({ label: selectedDepartment.name, href: `/?departmentId=${selectedDepartment.id}` });
  }
  if (selectedCategory) {
    const href = selectedDepartment
      ? `/?departmentId=${selectedDepartment.id}&categoryId=${selectedCategory.id}`
      : `/?categoryId=${selectedCategory.id}`;
    breadcrumbItems.push({ label: selectedCategory.name, href });
  }

  const handleSortChange = useCallback((newSort: string) => {
    localStorage.setItem(LS_SORT_KEY, newSort);
    const defaultSort = clientData?.defaultProductSort || 'prioritized';
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('page');
    if (newSort === defaultSort) {
      searchParams.delete('sort');
    } else {
      searchParams.set('sort', newSort);
    }
    navigate(`/?${searchParams.toString()}`);
  }, [location.search, navigate, clientData?.defaultProductSort]);

  return (
    <div className="mb-8">
      {isLoading ? (
        <>
          <nav className="mb-4" aria-hidden="true">
            <ol className="flex items-center space-x-2 text-sm animate-pulse">
              <li><div className="h-5 w-10 bg-gray-200 rounded" /></li>
              <li className="flex items-center">
                <div className="w-4 h-4 bg-gray-200 rounded mx-2" />
                <div className="h-5 w-24 bg-gray-200 rounded" />
              </li>
              {selectedCategoryId && (
                <li className="flex items-center">
                  <div className="w-4 h-4 bg-gray-200 rounded mx-2" />
                  <div className="h-5 w-28 bg-gray-200 rounded" />
                </li>
              )}
            </ol>
          </nav>
          <div className="animate-pulse">
            <div className="h-9 w-64 bg-gray-200 rounded mb-2" />
            <div className="h-6 w-96 bg-gray-200 rounded" />
          </div>
        </>
      ) : (
        <>
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {searchQuery
              ? `Search Results for "${searchQuery}"`
              : selectedCategory
                ? `${selectedCategory.name} Products`
                : selectedDepartment
                  ? `${selectedDepartment.name} Department`
                  : 'Featured Products'}
          </h1>
          <p className="text-gray-600">
            {searchQuery
              ? `Found ${total} ${total === 1 ? 'product' : 'products'} matching your search`
              : selectedCategory
                ? `Explore our ${selectedCategory.name.toLowerCase()} collection`
                : selectedDepartment
                  ? `Browse all products in the ${selectedDepartment.name} department`
                  : 'Discover our curated selection of premium products'}
          </p>
        </>
      )}

      <div className="mt-4 flex items-center gap-3">
        <label htmlFor="sort-select" className="text-xs font-semibold uppercase tracking-wider text-gray-400 whitespace-nowrap">
          Sort by
        </label>
        <div className="relative">
          <select
            id="sort-select"
            value={sortFromUrl}
            onChange={e => handleSortChange(e.target.value)}
            className="appearance-none text-sm text-gray-700 rounded-lg pl-3 pr-8 py-1.5 focus:outline-none bg-white border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
          >
            <option value="prioritized">Prioritized</option>
            <option value="alphabetical">Alphabetical</option>
          </select>
          <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </div>
  );
}
